<template>
    <div class="w-full mb-4 relative p-4">
        <!-- Header with Back Link and Title -->
        <div class="flex items-center justify-between mb-4">
            <router-link to="/settings" class="text-blue-600 hover:text-blue-800">
                <font-awesome-icon icon="fas fa-arrow-left" class="text-lg" />
            </router-link>
            <h2 class="text-xl font-semibold text-gray-800 dark:text-white">{{ $t('change_logs') }}</h2>
            <div class="text-sm text-gray-600 dark:text-gray-300">
                {{ $t('viewing_in_timezone') }}
                <strong>UTC {{ timeZone >= 0 ? '+' : '' }}{{ timeZone / 60 }} ({{ timeNow }})</strong>
            </div>
        </div>

        <section class="sm:flex items-center justify-between mb-4">
            <div class="flex overflow-x-auto mb-4 space-x-2 pb-2">
                <button
                    v-for="filter in filters"
                    :key="filter"
                    @click="setFilter(filter)"
                    :class="[
                        'px-4 py-1 sm:py-2 text-xs sm:text-base rounded-lg border-2 transition-colors whitespace-nowrap',
                        selectedFilter === filter
                            ? 'bg-blue-500 text-white border-blue-500'
                            : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                    ]"
                >
                    {{ $t(filter.toLowerCase().replace(/\s+/g, '_')) }}
                </button>
            </div>

            <div class="flex justify-end mb-4">
                <MazPicker
                    v-model="dateRange"
                    :label="$t('select_period')"
                    pickerPosition="right"
                    double
                />
            </div>
        </section>

        <!-- Logs Table -->
        <div class="overflow-y-auto max-h-[32rem] border rounded-lg lg:max-w-4xl xl:max-w-5xl 2xl:max-w-7xl">
            <table class="w-full text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800">
                <thead class="sticky top-0 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 border-b">
                    <tr>
                        <th class="px-4 py-2 border-r whitespace-nowrap">{{ $t('change_by') }}</th>
                        <th class="px-4 py-2 border-r whitespace-nowrap">{{ $t('change_on') }}</th>
                        <th class="px-4 py-2 border-r">{{ $t('date') }}</th>
                        <th class="px-4 py-2 border-r">{{ $t('time') }}</th>
                        <th class="px-4 py-2 border-r whitespace-nowrap">{{ $t('system_location') }}</th>
                        <th class="px-4 py-2 border-r whitespace-nowrap">{{ $t('original_value') }}</th>
                        <th class="px-4 py-2 border-r whitespace-nowrap">{{ $t('new_value') }}</th>
                        <th class="px-4 py-2">{{ $t('note') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(log, index) in displayedLogs" :key="log.id" :class="[index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-900' : 'bg-white dark:bg-gray-800', 'text-center']">
                        <td class="px-4 py-2">{{ log.employee }}</td>
                        <td class="px-4 py-2">{{ log.Changed_on }}</td>
                        <td class="px-4 py-2">{{ formatDateTime(log.datum, log.cas).date }}</td>
                        <td class="px-4 py-2">{{ formatDateTime(log.datum, log.cas).time }}</td>
                        <td class="px-4 py-2">{{ log.System_Location }}</td>
                        <td class="px-4 py-2 w-48">{{ log.Original_Value }}</td>
                        <td class="px-4 py-2 w-36">{{ log.New_Value }}</td>
                        <td class="px-4 py-2 w-48">{{ log.Note }}</td>
                    </tr>
                    <tr v-if="loading" class="text-center">
                        <td colspan="7" class="px-4 py-8">
                            <font-awesome-icon icon="fas fa-spinner" class="text-2xl text-blue-500 animate-spin" />
                        </td>
                    </tr>
                    <tr v-else-if="!displayedLogs">
                        <td colspan="7" class="px-4 py-2 text-center text-gray-500">{{ $t('no_data') }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Load More Button -->
        <div v-if="hasMore && !loading" class="text-center mt-4">
            <button @click="loadMore" class="px-4 py-2 bg-blue-500 text-white rounded-lg">
                {{ $t('load_more') }}
            </button>
        </div>
        <div v-else-if="hasMore && loading" class="text-center mt-4">
            <font-awesome-icon icon="fas fa-spinner" class="animate-spin" />
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import _ from 'lodash';

export default {
    name: 'Logs',
    data() {
        return {
            allLogs: [],              // Stores all fetched logs
            uniqueLocations: [],      // Stores unique System_Location values
            selectedFilter: 'All',    // Currently selected filter
            offset: 0,                // Offset for pagination
            hasMore: false,           // Indicates if more logs are available
            dateRange: null,          // Initialized as null to avoid immediate watch trigger
            timeNow: null,            // Current time for timezone display
            timeZone: null,           // User's timezone offset
            loading: false,           // Loading state for fetch operations
        };
    },
    computed: {
        /** Generates filter options including 'All' and unique System_Locations */
        filters() {
            return ['All', ...this.uniqueLocations];
        },
        /** Filters logs based on the selected filter for display */
        displayedLogs() {
            if (this.selectedFilter === 'All') {
                return this.allLogs;
            }
            return this.allLogs.filter(log => log.System_Location === this.selectedFilter);
        },
    },
    watch: {
        /** Reset and refetch logs when date range changes */
        dateRange: {
            handler: _.debounce(function(newVal) {
                if (newVal && newVal.start && newVal.end) { // Only trigger if both start and end are set
                    this.resetAndFetch();
                }
            }, 300),
            deep: true, // Watch for changes in nested properties
        },
    },
    methods: {
        /** Resets state and fetches initial logs */
        resetAndFetch() {
            this.allLogs = [];
            this.uniqueLocations = [];
            this.selectedFilter = 'All';
            this.offset = 0;
            this.hasMore = false;
            this.fetchLogs();
        },
        /** Fetches logs from the API with offset-based pagination */
        async fetchLogs() {
            this.loading = true; // Start loading
            try {
                const response = await this.$axios.get('/settingsget/', {
                    params: {
                        action: 'getLogs',
                        userToken: this.$cookies.get('userToken'),
                        companyID: this.$cookies.get('c_id'),
                        dateFrom: this.dateRange?.start ?? null,
                        dateTo: this.dateRange?.end ?? null,
                        offset: this.offset,
                    },
                });

                const newLogs = response.data.results;
                // Append new logs to existing logs
                this.allLogs = [...this.allLogs, ...newLogs];
                // Update unique System_Location values
                newLogs.forEach(log => {
                    if (!this.uniqueLocations.includes(log.System_Location)) {
                        this.uniqueLocations.push(log.System_Location);
                    }
                });
                // Update offset and hasMore
                this.offset = this.allLogs.length; // Offset is now the total number of logs fetched
                this.hasMore = response.data.hasMore;
            } catch (error) {
                console.error('Failed to fetch logs:', error);
            } finally {
                this.loading = false; // Stop loading regardless of success or failure
            }
        },
        /** Loads more logs when the button is clicked */
        loadMore() {
            this.fetchLogs();
        },
        /** Sets the selected filter for frontend filtering */
        setFilter(filter) {
            this.selectedFilter = filter;
        },
        /** Converts UTC date and time to local timezone */
        formatDateTime(datum, cas) {
            const utcDateTime = moment.utc(`${datum} ${cas}`, 'YYYY-MM-DD HH:mm:ss');
            const localDateTime = utcDateTime.utcOffset(this.timeZone);
            return {
                date: localDateTime.format('YYYY-MM-DD'),
                time: localDateTime.format('HH:mm:ss'),
            };
        },
    },
    created() {
        // Initialize timezone and current time
        this.timeZone = moment().utcOffset();
        this.timeNow = moment().format('HH:mm');

        // Set default date range (last 60 days to today)
        const defaultFrom = new Date();
        defaultFrom.setDate(defaultFrom.getDate() - 60);
        this.dateRange = {
            start: moment(defaultFrom).format('YYYY-MM-DD'),
            end: moment(new Date()).format('YYYY-MM-DD')
        };

        // Fetch logs on creation
        this.resetAndFetch();
    },
};
</script>

<style scoped>
.max-h-128 {
    max-height: 32rem; /* Tailwind's max-h-128 */
}
</style>